import React, { useCallback, useEffect, useState } from "react";
import Sidebar from "../../components/Sidebar/Sidebar";
import "katex/dist/katex.min.css";
import Latex from "react-latex-next";
import "../AssignmentPage/AssignmentPage.scss";
import { ZoomImage } from "../../components/ZoomImage/ZoomImage";
import { IoIosCheckmarkCircle } from "react-icons/io";
import { ImCross } from "react-icons/im";
import { FlaggedQuestion, Question } from "../../types";
import { AssignmentPageTitle } from "../AssignmentPage/AssignmentPageTitle";
import toast from "react-hot-toast";
import { toggleStarQuestion } from "../../utils/starQuestion";
import { shouldIgnoreQuestionNavigationShortcut } from "../../utils/shouldIgnoreQuestionNavigationShortcut";
import SubmissionPageSidebar from "../SubmissionPage/SubmissionPageSidebar";
import { SjtPracticeBlock } from "../../components/SJT/SjtPracticeBlock";
import { SjtOrderPanel } from "../../components/SJT/SjtOrderPanel";
import { computeSjtCorrect, getSjtSelectedOrderIds, isSjtQuestion } from "../../components/SJT/sjtUtils";
import { getOptionLetter } from "../../utils/optionLetters";
import { RolePlayPracticeBlock } from "../../components/RolePlay/RolePlayPracticeBlock";
import { EmqPartStepper } from "../../components/EmqPartStepper/EmqPartStepper";
import { isQuestionFlagged } from "../../utils/flagFeedback";

type ReadOnlyQuestionsViewProps = {
  questions: Question[];
  totalQuestionCount?: number;
  pageId?: string; // e.g., "ReviewPage" or "SubmissionPage"
  onQuestionsUpdate?: (updated: Question[]) => void;
  onApproachingEnd?: () => void | Promise<number>;
  isLoadingMore?: boolean;
};

function isRolePlayQuestion(question: Question): boolean {
  return question?.type?.toLowerCase?.() === "role_play";
}

export const ReadOnlyQuestionsView: React.FC<ReadOnlyQuestionsViewProps> = ({
  questions,
  totalQuestionCount,
  pageId = "ReadOnlyQuestions",
  onQuestionsUpdate,
  onApproachingEnd,
  isLoadingMore = false,
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const displayTotalQuestions = totalQuestionCount ?? questions.length;

  useEffect(() => {
    const element = document.getElementById(`q${currentQuestionIndex + 1}`);
    element?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [currentQuestionIndex]);

  useEffect(() => {
    if (!onApproachingEnd || questions.length === 0) {
      return;
    }

    if (currentQuestionIndex >= questions.length - 2) {
      onApproachingEnd();
    }
  }, [currentQuestionIndex, onApproachingEnd, questions.length]);

  const goToNextQuestion = useCallback(async () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      return;
    }

    if (displayTotalQuestions > questions.length && onApproachingEnd) {
      const added = await onApproachingEnd();
      if (added > 0) {
        setCurrentQuestionIndex((prev) => prev + 1);
      }
    }
  }, [
    currentQuestionIndex,
    displayTotalQuestions,
    onApproachingEnd,
    questions.length,
  ]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (shouldIgnoreQuestionNavigationShortcut(e)) {
        return;
      }

      if (e.key === "ArrowLeft") {
        if (currentQuestionIndex > 0) {
          setCurrentQuestionIndex(currentQuestionIndex - 1);
          e.preventDefault();
        }
      } else if (e.key === "ArrowRight") {
        if (currentQuestionIndex < questions.length - 1) {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
          e.preventDefault();
        } else if (displayTotalQuestions > questions.length) {
          void goToNextQuestion();
          e.preventDefault();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    currentQuestionIndex,
    displayTotalQuestions,
    goToNextQuestion,
    questions.length,
  ]);



  const currentQuestion = questions[currentQuestionIndex];
  const handleStarQuestion = async () => {
    try {
      const responseData = await toggleStarQuestion(currentQuestion.id);
      const updatedQuestions = questions.map((q) =>
        q.id === currentQuestion.id
          ? {
            ...q,
            starredBy: responseData.starredQuestion ? [responseData.starredQuestion] : [],
          }
          : q,
      );
      if (onQuestionsUpdate) {
        onQuestionsUpdate(updatedQuestions);
      } else {
        // Fallback: force re-render (should be replaced by parent-managed state)
        (questions as any).splice(0, questions.length, ...updatedQuestions);
        setCurrentQuestionIndex((prev) => prev);
      }
      toast.success(responseData.message);
    } catch (err) {
      toast.error("Sorry, error occurred starring this question. Please try again or seek support.");
    }
  };

  const applyFlaggedState = (flaggedQuestion: FlaggedQuestion) => {
    const updatedQuestions = questions.map((q) =>
      q.id === currentQuestion.id
        ? {
            ...q,
            flaggedBy: [flaggedQuestion],
            flaggedComments: [flaggedQuestion],
          }
        : q,
    );
    if (onQuestionsUpdate) {
      onQuestionsUpdate(updatedQuestions);
    } else {
      (questions as any).splice(0, questions.length, ...updatedQuestions);
      setCurrentQuestionIndex((prev) => prev);
    }
  };

  const handleFlagged = (flaggedQuestion: FlaggedQuestion) => {
    applyFlaggedState(flaggedQuestion);
  };

  if (!currentQuestion) {
    return (
      <div className="page-wrapper" id={pageId}>
        <Sidebar />
        <div className="internal-page-wrapper">No questions to display.</div>
      </div>
    );
  }

  const sjtSelection: number[] = isSjtQuestion(currentQuestion)
    ? getSjtSelectedOrderIds(currentQuestion)
    : [];

  // For role play questions, determine if the user selected "I got it right" or "I got it wrong"
  const rolePlaySelectedAnswer: boolean | undefined = isRolePlayQuestion(currentQuestion)
    ? (() => {
      const selectedOption = currentQuestion.answerOptions.find((opt) => opt.selected);
      return selectedOption ? selectedOption.isCorrect : undefined;
    })()
    : undefined;

  const answeredCorrectly = isSjtQuestion(currentQuestion)
    ? computeSjtCorrect(currentQuestion, sjtSelection)
    : isRolePlayQuestion(currentQuestion)
      ? rolePlaySelectedAnswer === true
      : currentQuestion.answerOptions.filter(
        (answerOption) => answerOption.isCorrect && answerOption.selected,
      ).length === 1;
  const noAnswerSubmittedForThisQuestion =
    currentQuestion.answerOptions.filter((answerOption) => answerOption.selected)
      .length === 0;

  const skippedInSubmission = isSjtQuestion(currentQuestion)
    ? sjtSelection.length === 0
    : isRolePlayQuestion(currentQuestion)
      ? rolePlaySelectedAnswer === undefined
      : noAnswerSubmittedForThisQuestion;

  const delimeters = [
    {
      display: false,
      left: "$$",
      right: "$$",
    },
  ];

  const explanationLines = currentQuestion.explanation
    ?.split("\\n")
    .map((line) => (
      <p className="explanation-line">
        <Latex delimiters={delimeters}>{line}</Latex>
      </p>
    ));

  return (
    <div className="page-wrapper" id={pageId}>
      <Sidebar />
      <div className="internal-page-wrapper">
        <main>
          <div className="inner-main-wrapper row">
            <div
              className="inner-max-width-wrapper col-10"
              style={{ position: "sticky", paddingRight: 0 }}
            >
              <div className="top-area">
                <AssignmentPageTitle
                  question={currentQuestion}
                  questionNumber={currentQuestionIndex + 1}
                  totalQuestions={displayTotalQuestions}
                  isAssignmentMode={false}
                  handleStarQuestion={handleStarQuestion}
                  onFlagged={handleFlagged}
                  isStarred={
                    currentQuestion.starredBy && currentQuestion.starredBy.length > 0
                  }
                  isFlagged={isQuestionFlagged(currentQuestion)}
                  rightExtra={pageId === "SubmissionPage" && skippedInSubmission ? (
                    <span className="badge bg-warning text-dark" style={{ fontSize: 12, padding: "6px 8px" }}>
                      This question was not answered
                    </span>
                  ) : undefined}
                  onNextQuestionClicked={goToNextQuestion}
                  nextQuestionButtonDisabled={
                    currentQuestionIndex === questions.length - 1 &&
                    displayTotalQuestions <= questions.length
                  }
                  currentQuestionIndex={currentQuestionIndex}
                  onPreviousQuestionClicked={() => {
                    setCurrentQuestionIndex(currentQuestionIndex - 1);
                  }}
                  prevQuestionButtonDisabled={currentQuestionIndex === 0}
                />
              </div>

              <div
                id="scroller"
                className={
                  isRolePlayQuestion(currentQuestion) ? "scroller--role-play" : undefined
                }
              >
                <div
                  style={{
                    display: "flex",
                    gap: 24,
                    alignItems: "stretch",
                    backgroundColor: isRolePlayQuestion(currentQuestion) ? "white" : "#f8f9fa",
                    padding: "30px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    {!isRolePlayQuestion(currentQuestion) && (
                      <div className="questionTitle" style={{ lineHeight: 1.5 }}>
                        {currentQuestion.emqPart?.partOrder != null &&
                        currentQuestion.emqPart?.partsCount != null ? (
                          <EmqPartStepper
                            partOrder={currentQuestion.emqPart.partOrder}
                            partsCount={currentQuestion.emqPart.partsCount}
                          />
                        ) : null}
                        <Latex delimiters={delimeters}>{currentQuestion.text}</Latex>
                      </div>
                    )}

                    {currentQuestion.imageUrl && !isRolePlayQuestion(currentQuestion) && (
                      <div style={{ marginBottom: "50px" }}>
                        <ZoomImage src={currentQuestion.imageUrl} width={300}></ZoomImage>
                      </div>
                    )}

                    {isRolePlayQuestion(currentQuestion) ? (
                      <RolePlayPracticeBlock
                        question={currentQuestion}
                        onSubmit={() => { }}
                        isAnswered={true}
                        showMarkScheme={true}
                        onToggleMarkScheme={() => { }}
                        selectedAnswer={rolePlaySelectedAnswer}
                      />
                    ) : isSjtQuestion(currentQuestion) ? (
                      <SjtPracticeBlock
                        question={currentQuestion}
                        practiceQuestionAnswered={true}
                        selection={sjtSelection}
                        delimeters={delimeters}
                        onSelectionChange={() => { }}
                        onSubmit={() => { }}
                      />
                    ) : (
                      <div className="answer-options-wrapper">
                        {currentQuestion?.answerOptions.map((answerOption, index) => {
                          const option = getOptionLetter(answerOption, index);
                          const classes = ["answer-option", "locked"] as string[];

                          if (answerOption.isCorrect) {
                            classes.push("answer-option--correct");
                          }
                          if (answerOption.selected) {
                            classes.push("selected-answer-option");
                            classes.push(
                              answerOption.isCorrect
                                ? "selected-answer-option--correct"
                                : "selected-answer-option--incorrect",
                            );
                          }

                          return (
                            <div
                              className={classes.join(" ")}
                              id={answerOption.id.toString()}
                            >
                              <div>
                                <div
                                  className="top-part"
                                  style={{ display: "flex", alignItems: "center" }}
                                >
                                  <label htmlFor={option}>{option}</label>

                                  <div style={{ marginLeft: "10px" }}>
                                    <Latex delimiters={delimeters}>
                                      {answerOption.text}
                                    </Latex>
                                  </div>

                                  <span
                                    style={{
                                      marginLeft: "auto",
                                      display: "inline-flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    {answerOption.isCorrect ? (
                                      <IoIosCheckmarkCircle color="green" size={25} />
                                    ) : (
                                      <ImCross
                                        color="white"
                                        size={20}
                                        style={{
                                          backgroundColor: "red",
                                          borderRadius: "10px",
                                          padding: "5px",
                                        }}
                                      />
                                    )}
                                  </span>

                                  <input
                                    type="checkbox"
                                    disabled
                                    name={option}
                                    id={option}
                                    style={{ display: "none" }}
                                  />
                                </div>

                                {answerOption.imageUrl && (
                                  <div className="bottom-part">
                                    <ZoomImage
                                      src={answerOption.imageUrl}
                                      width={200}
                                    ></ZoomImage>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>


                </div>
                {!isRolePlayQuestion(currentQuestion) && (
                  <div style={{ flex: 1 }}>
                    <div
                      className={
                        answeredCorrectly
                          ? "explanation explanation-correct"
                          : "explanation explanation-incorrect"
                      }
                    >
                      <div className="explanation-title">
                        {answeredCorrectly ? (
                          <span>
                            <IoIosCheckmarkCircle
                              color="green"
                              size={25}
                              style={{ position: "relative", bottom: "2px" }}
                            />
                            {" "}Correct!
                          </span>
                        ) : (
                          <span>
                            <ImCross
                              color="white"
                              size={20}
                              style={{
                                backgroundColor: "red",
                                borderRadius: "10px",
                                padding: "5px",
                                position: "relative",
                                bottom: "2px",
                              }}
                            />
                            {" "}Incorrect!
                          </span>
                        )}
                      </div>

                      {isSjtQuestion(currentQuestion) && (
                        <SjtOrderPanel
                          question={currentQuestion}
                          selection={sjtSelection}
                        />
                      )}

                      <div className="explanation-box">
                        <div className="explanation-box-label">Explanation</div>
                        {explanationLines}
                      </div>

                      <div style={{ display: "flex", gap: 20, marginTop: 20 }}>
                        {currentQuestion.explanationImageUrl ? (
                          <ZoomImage
                            src={currentQuestion.explanationImageUrl}
                            width={150}
                          ></ZoomImage>
                        ) : undefined}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="col-2" style={{ paddingLeft: 0 }}>
              <SubmissionPageSidebar
                submittedPracticeQuestions={questions}
                totalQuestionCount={displayTotalQuestions}
                isLoadingMore={isLoadingMore}
                onSubmittedQuestionClicked={(index: number) => {
                  setCurrentQuestionIndex(index);
                }}
                currentQuestionIndex={currentQuestionIndex}
              />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ReadOnlyQuestionsView;


